import { GoogleAnalyticsDimensionsAndMetrics } from "./GoogleAnalyticsDimentionsAndMetrics";
import { GoogleAnalyticsDimensionsAndMetricsProvider } from "./GoogleAnalyticsDimentionsAndMetricsProvider";
import { getCookieByName } from "@ado/cookie-library";
import { QueryStringValuesAccessor } from "../query_string/QueryStringValuesAccessor";
import { DocumentWrapper } from "../document_wrapping/DocumentWrapper";
import { AnalyticsPageNameProvider } from "../page_name/AnalyticsPageNameProvider";
import { ServiceProvider } from "../../dependencies_management/ServiceProvider";
import { ExecutionContext } from "../../types/ExecutionContext";

export class DefaultGoogleAnalyticsDimensionsAndMetricsProvider
	implements GoogleAnalyticsDimensionsAndMetricsProvider
{
	private readonly analyticsPageNameProvider: AnalyticsPageNameProvider;
	private readonly queryStringValuesAccessor: QueryStringValuesAccessor;
	private readonly document: DocumentWrapper;

	constructor(serviceProvider: ServiceProvider) {
		this.analyticsPageNameProvider =
			serviceProvider.getAnalyticsPageNameProvider();
		this.queryStringValuesAccessor =
			serviceProvider.getQueryStringValuesAccessor();
		this.document = serviceProvider.getDocumentWrapper();
	}

	provideDimensionsAndMetrics(
		executionContext: ExecutionContext,
		isUserLicensed: boolean
	): GoogleAnalyticsDimensionsAndMetrics {
		const semicolonSeparatedExperiments =
			this.getSemicolonSeparatedExperimentsFromCookies();
		const location = getCookieByName("L") ?? "";
		const file = this.queryStringValuesAccessor.getValueByKey("file");
		const doNotTrack = this.getDoNotTrackValue();
		const isUserLicensedString = isUserLicensed ? "1" : "0";
		/* GA rejects the page name if it doesn't start with a slash */
		const pageName = this.addLeadingSlashIfNotPresent(
			this.getAnalyticsPageName(executionContext)
		);
		const pageTitle =
			pageName ?? this.document.windowLocation.pathWithQueryString;

		const hasLoginIdIssue =
			executionContext.loginId === undefined &&
			semicolonSeparatedExperiments.length > 0;

		return {
			semicolonSeparatedExperiments,
			location,
			file,
			isUserLicensed: isUserLicensedString,
			doNotTrack,
			pageName,
			pageTitle,
			hasLoginIdIssue,
		};
	}

	private getSemicolonSeparatedExperimentsFromCookies(): string {
		let semicolonSeparatedExperiments = getCookieByName("dcsExp") ?? "";
		semicolonSeparatedExperiments = this.removeTrailingSemicolonIfPresent(
			semicolonSeparatedExperiments
		);

		if (semicolonSeparatedExperiments.length > 116) {
			semicolonSeparatedExperiments = semicolonSeparatedExperiments.substring(
				0,
				116
			);
		}
		return semicolonSeparatedExperiments;
	}

	private getDoNotTrackValue(): string {
		return navigator.doNotTrack === "1" ||
			navigator.doNotTrack === "yes" ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(navigator as any).msDoNotTrack === "1" ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).doNotTrack === "1"
			? "1"
			: "0";
	}

	private addLeadingSlashIfNotPresent(
		value: string | undefined
	): string | undefined {
		return value !== undefined && !value.startsWith("/") ? "/" + value : value;
	}

	private getAnalyticsPageName(
		executionContext: ExecutionContext
	): string | undefined {
		return this.analyticsPageNameProvider.providePageName(executionContext);
	}

	private isLastCharacterSemicolon(value: string): boolean {
		return value.length > 0 && value.slice(-1) === ";";
	}

	private removeTrailingSemicolonIfPresent(value: string): string {
		return this.isLastCharacterSemicolon(value)
			? value.substring(0, value.length - 1)
			: value;
	}
}
